import type { AiSuggestion } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { listSuggestions, rejectSuggestion } from '../api/ai.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ActionDialog } from '../components/ActionDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { AddressLink } from '../components/AddressStatus.js';
import { PageHeader } from '../components/PageHeader.js';
import { SeverityBadge } from '../components/StatusBadge.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { Dialog } from '../ui/Dialog.js';
import { TextField } from '../ui/Field.js';

/**
 * AI suggestions (section 13; P9.3): what a model made of an incident, and the
 * ban it proposes, for a person to approve or reject.
 *
 * Anyone who can read events reads them; deciding is an operator's. Approving
 * opens the ban dialog with the suggestion's address locked and its preview, so
 * an approved suggestion is exactly a manual ban — the same checks, the same
 * commands, the same audit — marked as having come from AI.
 */

const RECOMMENDATION_TEXT: Record<AiSuggestion['recommendation'], string> = {
  ban: 'Recommends a ban',
  watch: 'Recommends watching',
  ignore: 'Sees nothing to do',
};

const hours = (seconds: number): string => {
  const h = Math.round(seconds / 3600);
  return h % 24 === 0 ? `${String(h / 24)} ${h === 24 ? 'day' : 'days'}` : `${String(h)} hours`;
};

export function SuggestionsPage(): ReactNode {
  const [view, setView] = useState<'open' | 'all'>('open');
  const suggestions = useQuery({
    queryKey: ['suggestions', view],
    queryFn: () => listSuggestions(view === 'open' ? 'open' : undefined),
  });

  return (
    <div className="page">
      <PageHeader
        title="Suggestions"
        description="What a language model made of an incident, and the ban it proposes. Nothing happens until a person approves it, and then it is a ban like any other."
      />
      <div className="row">
        <Button
          variant={view === 'open' ? 'primary' : 'ghost'}
          onClick={() => {
            setView('open');
          }}
        >
          Waiting for a decision
        </Button>
        <Button
          variant={view === 'all' ? 'primary' : 'ghost'}
          onClick={() => {
            setView('all');
          }}
        >
          All
        </Button>
      </div>
      {suggestions.data?.length === 0 && (
        <EmptyState title={view === 'open' ? 'Nothing waiting' : 'No suggestions yet'}>
          Ask for one from an event, or have an admin turn on suggestions for attacks as they
          arrive.
        </EmptyState>
      )}
      {(suggestions.data ?? []).map((suggestion) => (
        <SuggestionCard key={suggestion.id} suggestion={suggestion} />
      ))}
    </div>
  );
}

function SuggestionCard({ suggestion }: { suggestion: AiSuggestion }): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const canDecide = session.user.role !== 'viewer';
  const queryClient = useQueryClient();
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const open = suggestion.status === 'proposed' && Date.parse(suggestion.expiresAt) > Date.now();
  const proposal = suggestion.proposedAction;
  const firstServer = suggestion.servers[0];

  const reject = useMutation({
    mutationFn: () => rejectSuggestion(suggestion.id, note),
    onSuccess: () => {
      setRejecting(false);
      void queryClient.invalidateQueries({ queryKey: ['suggestions'] });
    },
  });

  return (
    <Card
      title={suggestion.title}
      aside={<SeverityBadge severity={suggestion.priority} />}
      footer={
        <span className="cell-sub">
          {suggestion.providerName} · {suggestion.model} ·{' '}
          <span title={formatInstant(suggestion.createdAt, { timezone })}>
            {formatRelative(suggestion.createdAt)}
          </span>
          {suggestion.origin === 'automatic'
            ? ' · made on its own'
            : suggestion.requestedByEmail !== null && ` · asked by ${suggestion.requestedByEmail}`}
          {open && ` · expires ${formatRelative(suggestion.expiresAt)}`}
        </span>
      }
    >
      <div className="stack">
        <p>{suggestion.summary}</p>
        <p>
          <strong>{RECOMMENDATION_TEXT[suggestion.recommendation]}</strong>
          {suggestion.subjectIp !== null && (
            <>
              {' '}
              for <AddressLink ip={suggestion.subjectIp} />
            </>
          )}
          {suggestion.servers.length > 0 &&
            `, seen on ${suggestion.servers.map((server) => server.name).join(', ')}`}
          .
        </p>
        {proposal !== null && (
          <p>
            Proposed: ban <code>{proposal.ip}</code> on every server for{' '}
            {hours(proposal.duration_seconds)} — “{proposal.reason}”.
          </p>
        )}
        {suggestion.recommendation === 'ban' && proposal === null && (
          <Alert tone="info">This incident has no address, so there is nothing to ban.</Alert>
        )}
        {suggestion.relatedEventIds.length > 0 && (
          <p className="text-sm">
            Events:{' '}
            {suggestion.relatedEventIds.slice(0, 5).map((id, index) => (
              <span key={id}>
                {index > 0 && ', '}
                <Link to={`/events/${encodeURIComponent(id)}`}>{String(index + 1)}</Link>
              </span>
            ))}
            {suggestion.relatedEventIds.length > 5 &&
              ` and ${String(suggestion.relatedEventIds.length - 5)} more`}
          </p>
        )}
        {suggestion.status === 'approved' && suggestion.actionId !== null && (
          <Alert tone="success">
            Approved by {suggestion.reviewedByEmail ?? 'someone'}:{' '}
            <Link to={`/actions/${encodeURIComponent(suggestion.actionId)}`}>see the ban</Link>.
          </Alert>
        )}
        {suggestion.status === 'rejected' && (
          <Alert tone="info">
            Rejected by {suggestion.reviewedByEmail ?? 'someone'}
            {suggestion.reviewNote !== null && `: ${suggestion.reviewNote}`}
          </Alert>
        )}
        {(suggestion.status === 'expired' || (suggestion.status === 'proposed' && !open)) && (
          <Alert tone="info">Expired unanswered.</Alert>
        )}
        {canDecide && open && (
          <div className="row">
            {proposal !== null && firstServer !== undefined && (
              <Button
                variant="danger"
                onClick={() => {
                  setApproving(true);
                }}
              >
                Approve the ban…
              </Button>
            )}
            <Button
              onClick={() => {
                setRejecting(true);
              }}
            >
              {proposal === null ? 'Dismiss' : 'Reject'}
            </Button>
          </div>
        )}
      </div>
      {approving && proposal !== null && firstServer !== undefined && (
        <ActionDialog
          type="ban_ip"
          server={{ id: firstServer.id, name: firstServer.name, group: null }}
          suggestion={{
            id: suggestion.id,
            ip: proposal.ip,
            durationSeconds: proposal.duration_seconds,
            reason: proposal.reason,
          }}
          onClose={() => {
            setApproving(false);
          }}
        />
      )}
      {rejecting && (
        <Dialog
          title={proposal === null ? 'Dismiss this suggestion?' : 'Reject this suggestion?'}
          description="Nothing is sent to any server. A note says why, for whoever reads it later."
          onClose={() => {
            setRejecting(false);
          }}
        >
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              reject.mutate();
            }}
          >
            <TextField
              label="Note (optional)"
              maxLength={500}
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
            {reject.isError && (
              <Alert tone="danger">
                {reject.error instanceof Error ? reject.error.message : 'It did not go through.'}
              </Alert>
            )}
            <div className="dialog-actions">
              <Button
                onClick={() => {
                  setRejecting(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" busy={reject.isPending} busyLabel="Saving…">
                {proposal === null ? 'Dismiss' : 'Reject'}
              </Button>
            </div>
          </form>
        </Dialog>
      )}
    </Card>
  );
}
